import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * Hierarchical injury picker.
 *
 * Broad regions (Shoulder, Elbow, Knee...) are always visible as chips.
 * Tapping the small "+" affordance on a region expands a panel showing
 * that region's specific pathologies (e.g. Shoulder → Rotator cuff,
 * Biceps tendinitis (long head), AC joint...).
 *
 * When the user picks a specific, the parent broad region is also
 * activated automatically so safety filtering cascades — the specific
 * IDs live in the exercise library as extra contraindication tags,
 * layered on top of the broad ones.
 *
 * Props:
 *   value:    string[]  currently-selected injury IDs (broad + specifics)
 *   onChange: (next: string[]) => void
 */
export default function InjuryPicker({ value = [], onChange }) {
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState({}); // { region: bool }

  useEffect(() => {
    api.injuryTaxonomy()
      .then((d) => setTree(d?.regions || []))
      .catch(() => setTree([]));
  }, []);

  if (!tree) {
    return (
      <div className="text-[#7c8597] text-xs" data-testid="injury-picker-loading">Loading…</div>
    );
  }

  const has = (id) => value.includes(id);

  const toggleRegion = (region) => {
    // Toggling a broad chip: on-select adds the region; on-deselect removes
    // the region AND all of its specifics (specifics are meaningless
    // without their parent).
    if (has(region)) {
      const specifics = tree.find((t) => t.region === region)?.specifics || [];
      const ids = new Set([region, ...specifics.map((s) => s.id)]);
      onChange(value.filter((v) => !ids.has(v)));
    } else {
      onChange([...value, region]);
    }
  };

  const toggleSpecific = (region, specId) => {
    // Toggling a specific: on-select adds specific + ensures parent region
    // is present (inheritance). On-deselect removes only the specific.
    if (has(specId)) {
      onChange(value.filter((v) => v !== specId));
    } else {
      const next = [...value, specId];
      if (!has(region)) next.push(region);
      onChange(next);
    }
  };

  const toggleExpand = (region) => {
    setExpanded((prev) => ({ ...prev, [region]: !prev[region] }));
  };

  return (
    <div className="space-y-2" data-testid="injury-picker">
      {tree.map(({ region, specifics }) => {
        const active = has(region);
        const isOpen = !!expanded[region];
        const selectedSpecifics = specifics.filter((s) => has(s.id));
        return (
          <div
            key={region}
            data-testid={`injury-region-${region.toLowerCase().replace(/[^a-z]/g, '-')}`}
            className={`rounded-xl border transition-colors ${
              active ? 'border-[#ff7a1a]/50 bg-[#ff7a1a]/5' : 'border-[#2a2f3a] bg-[#0b0c10]'
            }`}
          >
            <div className="flex items-center gap-2 px-3 py-2">
              <button
                type="button"
                onClick={() => toggleRegion(region)}
                data-testid={`injury-region-${region.toLowerCase().replace(/[^a-z]/g, '-')}-toggle`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-[#ff7a1a] text-black'
                    : 'bg-[#12141a] text-[#cbd5e1] hover:bg-[#1a1d24]'
                }`}
              >
                {active && <Check className="w-3 h-3" strokeWidth={3} />}
                {region}
                {selectedSpecifics.length > 0 && (
                  <span className="ml-1 px-1.5 rounded-full bg-black/25 text-[10px] font-bold tabular-nums">
                    {selectedSpecifics.length}
                  </span>
                )}
              </button>
              <div className="flex-1" />
              {specifics.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleExpand(region)}
                  data-testid={`injury-region-${region.toLowerCase().replace(/[^a-z]/g, '-')}-expand`}
                  className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597] hover:text-[#cbd5e1] flex items-center gap-1"
                  aria-expanded={isOpen}
                >
                  {isOpen ? 'Hide' : 'More specific?'}
                  {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>
            {isOpen && specifics.length > 0 && (
              <div className="border-t border-[#2a2f3a] px-3 py-2.5">
                <div className="flex flex-wrap gap-1.5">
                  {specifics.map((s) => {
                    const on = has(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSpecific(region, s.id)}
                        data-testid={`injury-specific-${s.id.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          on
                            ? 'bg-[#BEFF38] text-black'
                            : 'bg-[#12141a] text-[#aab1bf] hover:bg-[#1a1d24] hover:text-white'
                        }`}
                      >
                        {on && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-[#7c8597] italic leading-snug">
                  Picking a specific pathology keeps the broad region on too — safer default.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
